import type { JSX } from 'react';
import { useState } from 'react';
import { toast } from 'sonner';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { SuperAdminActionsMenu } from './SuperAdminActionsMenu';
import { useSuperAdminStatus } from '../hooks/useSuperAdminStatus';
import type { SuperAdmin } from '../types/super-admin.types';
import { type ConfirmAction, buildConfirmHandler } from './SuperAdminActions.utils';

interface SuperAdminActionsProps {
  superAdmin: SuperAdmin;
  onEdit: (superAdmin: SuperAdmin) => void;
  isCurrentUser?: boolean | undefined;
}

export function SuperAdminActions({
  superAdmin,
  onEdit,
  isCurrentUser,
}: SuperAdminActionsProps): JSX.Element | null {
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const { deactivate, activate, sendPasswordReset, isDeactivating, isActivating, isSendingReset } =
    useSuperAdminStatus();

  if (isCurrentUser) return null;

  const handleConfirm = buildConfirmHandler({
    confirmAction,
    sendPasswordReset,
    deactivate,
    userId: superAdmin.id,
    clearAction: () => setConfirmAction(null),
  });

  const handleActivate = (): void => {
    activate(superAdmin.id, {
      onSuccess: () => toast.success('Super admin activated'),
      onError: () => toast.error('Failed to activate super admin'),
    });
  };

  return (
    <>
      <SuperAdminActionsMenu
        superAdmin={superAdmin}
        onEdit={onEdit}
        isDisabled={isDeactivating || isActivating || isSendingReset}
        onResetPassword={() => setConfirmAction('resetPassword')}
        onDeactivate={() => setConfirmAction('deactivate')}
        onActivate={handleActivate}
      />
      <ConfirmActionDialog
        action={confirmAction}
        email={superAdmin.email}
        fullName={superAdmin.profile.fullName}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleConfirm}
      />
    </>
  );
}
